import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _uniq from "lodash/uniq";
import _isString from "lodash/isString";
import _isNil from "lodash/isNil";

var _scaleMap, _dec, _dec2, _class, _class2, _descriptor;

import { ScaleTypes, StyleScaleType, TYPES } from '@antv/l7-core';
import { extent } from 'd3-array';
import * as d3 from 'd3-scale';
import { inject, injectable } from 'inversify';
import 'reflect-metadata';
var dateRegex = /^(?:(?!0000)[0-9]{4}([-/.]+)(?:(?:0?[1-9]|1[0-2])\1(?:0?[1-9]|1[0-9]|2[0-8])|(?:0?[13-9]|1[0-2])\1(?:29|30)|(?:0?[13578]|1[02])\1(?:31))|(?:[0-9]{2}(?:0[48]|[2468][048]|[13579][26])|(?:0[48]|[2468][048]|[13579][26])00)([-/.]?)0?2\2(?:29))(\s+([01]|([01][0-9]|2[0-3])):([0-9]|[0-5][0-9]):([0-9]|[0-5][0-9]))?$/;
var scaleMap = (_scaleMap = {}, _defineProperty(_scaleMap, ScaleTypes.LINEAR, d3.scaleLinear), _defineProperty(_scaleMap, ScaleTypes.POWER, d3.scalePow), _defineProperty(_scaleMap, ScaleTypes.LOG, d3.scaleLog), _defineProperty(_scaleMap, ScaleTypes.IDENTITY, d3.scaleIdentity), _defineProperty(_scaleMap, ScaleTypes.TIME, d3.scaleTime), _defineProperty(_scaleMap, ScaleTypes.QUANTILE, d3.scaleQuantile), _defineProperty(_scaleMap, ScaleTypes.QUANTIZE, d3.scaleQuantize), _defineProperty(_scaleMap, ScaleTypes.THRESHOLD, d3.scaleThreshold), _defineProperty(_scaleMap, ScaleTypes.CAT, d3.scaleOrdinal), _scaleMap);
var FeatureScalePlugin = (_dec = injectable(), _dec2 = inject(TYPES.IGlobalConfigService), _dec(_class = (_class2 = function () {
  function FeatureScalePlugin() {
    _classCallCheck(this, FeatureScalePlugin);

    _initializerDefineProperty(this, "configService", _descriptor, this);

    _defineProperty(this, "scaleCache", {});

    _defineProperty(this, "scaleOptions", {});
  }

  _createClass(FeatureScalePlugin, [{
    key: "apply",
    value: function apply(layer, _ref) {
      var _this = this;

      var styleAttributeService = _ref.styleAttributeService;
      layer.hooks.init.tap('FeatureScalePlugin', function () {
        _this.scaleOptions = layer.getScaleOptions();
        var attributes = styleAttributeService.getLayerStyleAttributes();
        var dataArray = layer.getSource().data.dataArray;

        if (dataArray.length === 0) {
          return;
        }

        _this.caculateScalesForAttributes(attributes || [], dataArray);
      });
      layer.hooks.beforeRenderData.tap('FeatureScalePlugin', function () {
        _this.scaleOptions = layer.getScaleOptions();
        var attributes = styleAttributeService.getLayerStyleAttributes();
        var dataArray = layer.getSource().data.dataArray;

        _this.caculateScalesForAttributes(attributes || [], dataArray);

        layer.layerModelNeedUpdate = true;
        return true;
      });
      layer.hooks.beforeRender.tap('FeatureScalePlugin', function () {
        if (layer.layerModelNeedUpdate) {
          return;
        }

        _this.scaleOptions = layer.getScaleOptions();
        var attributes = styleAttributeService.getLayerStyleAttributes();

        if (attributes) {
          var dataArray = layer.getSource().data.dataArray;

          if (dataArray.length === 0) {
            return;
          }

          var attributesToRescale = attributes.filter(function (attribute) {
            return attribute.needRescale;
          });

          if (attributesToRescale.length) {
            _this.caculateScalesForAttributes(attributesToRescale, dataArray);
          }
        }
      });
    }
  }, {
    key: "isNumber",
    value: function isNumber(n) {
      return !isNaN(parseFloat(n)) && isFinite(n);
    }
  }, {
    key: "caculateScalesForAttributes",
    value: function caculateScalesForAttributes(attributes, dataArray) {
      var _this2 = this;

      this.scaleCache = {};
      attributes.forEach(function (attribute) {
        if (attribute.scale) {
          var attributeScale = attribute.scale;
          var type = attribute.name;
          attributeScale.names = _this2.parseFields(attribute.scale.field || []);
          var scales = [];
          attributeScale.names.forEach(function (field) {
            scales.push(_this2.getOrCreateScale(field, attribute, dataArray));
          });

          if (scales.some(function (scale) {
            return scale.type === StyleScaleType.VARIABLE;
          })) {
            attributeScale.type = StyleScaleType.VARIABLE;
            scales.forEach(function (scale) {
              if (!attributeScale.callback) {
                var _scale$option2;

                if (attributeScale.values && attributeScale.values !== 'text') {
                  var _scale$option;

                  if (((_scale$option = scale.option) === null || _scale$option === void 0 ? void 0 : _scale$option.type) === 'linear' && attributeScale.values.length > 2) {
                    var tick = scale.scale.ticks(attributeScale.values.length);

                    if (type === 'color') {
                      scale.scale.domain(tick);
                    }
                  }

                  scale.scale.range(attributeScale.values);
                } else if (((_scale$option2 = scale.option) === null || _scale$option2 === void 0 ? void 0 : _scale$option2.type) === 'cat') {
                  scale.scale.range(scale.option.domain);
                }
              }
            });
          } else {
            attributeScale.type = StyleScaleType.CONSTANT;
            attributeScale.defaultValues = scales.map(function (scale, index) {
              return scale.scale(attributeScale.names[index]);
            });
          }

          attributeScale.scalers = scales.map(function (scale) {
            return {
              field: scale.field,
              func: scale.scale,
              option: scale.option
            };
          });
          attribute.needRescale = false;
        }
      });
    }
  }, {
    key: "getOrCreateScale",
    value: function getOrCreateScale(field, attribute, dataArray) {
      var _attribute$scale;

      var scalekey = [field, attribute.name].join('_');
      var values = (_attribute$scale = attribute.scale) === null || _attribute$scale === void 0 ? void 0 : _attribute$scale.values;
      var styleScale = this.createScale(field, attribute.name, values, dataArray);
      return styleScale;
    }
  }, {
    key: "parseFields",
    value: function parseFields(field) {
      if (Array.isArray(field)) {
        return field;
      }

      if (_isString(field)) {
        return field.split('*');
      }

      return [field];
    }
  }, {
    key: "createScale",
    value: function createScale(field, name, values, data) {
      var _this$scaleOptions$na, _find;

      var scaleOption = this.scaleOptions[name] && ((_this$scaleOptions$na = this.scaleOptions[name]) === null || _this$scaleOptions$na === void 0 ? void 0 : _this$scaleOptions$na.field) === field ? this.scaleOptions[name] : this.scaleOptions[field];
      var styleScale = {
        field: field,
        scale: undefined,
        type: StyleScaleType.VARIABLE,
        option: scaleOption
      };

      if (!data || !data.length) {
        if (scaleOption && scaleOption.type) {
          styleScale.scale = this.createDefaultScale(scaleOption);
        } else {
          styleScale.scale = d3.scaleOrdinal([field]);
          styleScale.type = StyleScaleType.CONSTANT;
        }

        return styleScale;
      }

      var firstValue = (_find = data.find(function (d) {
        return !_isNil(d[field]);
      })) === null || _find === void 0 ? void 0 : _find[field];

      if (this.isNumber(field) || _isNil(firstValue) && !scaleOption) {
        styleScale.scale = d3.scaleOrdinal([field]);
        styleScale.type = StyleScaleType.CONSTANT;
      } else {
        var type = scaleOption && scaleOption.type || this.getDefaultType(firstValue);

        if (values === 'text') {
          type = ScaleTypes.CAT;
        }

        var cfg = this.createDefaultScaleConfig(type, field, data);
        Object.assign(cfg, scaleOption);
        styleScale.scale = this.createDefaultScale(cfg);
        styleScale.option = cfg;
      }

      return styleScale;
    }
  }, {
    key: "getDefaultType",
    value: function getDefaultType(firstValue) {
      var type = ScaleTypes.LINEAR;

      if (typeof firstValue === 'string') {
        type = dateRegex.test(firstValue) ? ScaleTypes.TIME : ScaleTypes.CAT;
      }

      return type;
    }
  }, {
    key: "createDefaultScaleConfig",
    value: function createDefaultScaleConfig(type, field, data) {
      var cfg = {
        type: type
      };
      var values = (data === null || data === void 0 ? void 0 : data.map(function (item) {
        return item[field];
      })) || [];

      if (type !== ScaleTypes.CAT && type !== ScaleTypes.QUANTILE) {
        cfg.domain = extent(values);
      } else if (type === ScaleTypes.CAT) {
        cfg.domain = _uniq(values);
      } else if (type === ScaleTypes.QUANTILE) {
        cfg.domain = values;
      }

      return cfg;
    }
  }, {
    key: "createDefaultScale",
    value: function createDefaultScale(_ref2) {
      var type = _ref2.type,
          domain = _ref2.domain;
      var scale = scaleMap[type]();

      if (domain) {
        scale.domain(domain);
      }

      return scale;
    }
  }]);

  return FeatureScalePlugin;
}(), (_descriptor = _applyDecoratedDescriptor(_class2.prototype, "configService", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
})), _class2)) || _class);
export { FeatureScalePlugin as default };
//# sourceMappingURL=FeatureScalePlugin.js.map